import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { AdditionalTypes } from '../../modules/additionalTypes'
import { ConsoleLog } from '../../modules/logging'
import MainModule from '../../modules/main'

Locale.getInstance().registerKeySet('fr', 'lvc-representation-block', {
  startingFrom: 'À partir de ',
  modify: 'Modifier',
  free: 'Gratuit',
  exhibitionTypeLabel: 'Exposition',
  offerTypeLabel: 'Représentation',
})

Locale.getInstance().registerKeySet('en', 'lvc-representation-block', {
  startingFrom: 'Starting from ',
  modify: 'Modify',
  free: 'Free',
  exhibitionTypeLabel: 'Exhibition',
  offerTypeLabel: 'Representation',
})

export interface LvcRepresentationBlock {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
  representationData: any
  startDate: string
  endDate: string
  startTime: { [lang: string]: string } | string
  flowGroup: string
  isExisting: boolean
  priceData: {
    price: string
    priceType: string
  }
}

export class LvcRepresentationBlock extends MinzeElement {
  private disciplines: any[] = []
  private selectedIds: string[] = []
  private filter: string = ''
  attrs: Attrs = [
    'title',
    'placeholder',
    ['default-value', ''],
    ['on-change', ''],
    ['representation-data', ''],
    ['representation-index', ''],
    ['flow-group', ''],
    ['is-existing', false],
    ['show-type', false],
    ['hide-price', false],
    ['compact', false],
  ]

  reactive: Reactive = [
    ['value', ''],
    ['dateTimeData', ''],
    ['locationNameData', ''],
    ['locationData', {}],
    ['priceData', ''],
    ['startDate', ''],
    ['endDate', ''],
    ['startTime', ''],
    ['startDateFormatted', ''],
    ['address', ''],
    ['city', ''],
    ['province', ''],
    ['zipcode', ''],
    ['physicalLocationData', {}],
    ['virtualLocationData', {}],
    ['placeAddress', ''],
    ['placeCity', ''],
    ['placeRegion', ''],
    ['placePostalCode', ''],
    ['virtualPlaceAddress', ''],
    ['virtualPlaceCity', ''],
    ['virtualPlaceRegion', ''],
    ['virtualPlacePostalCode', ''],
    ['place', ''],
    ['address', ''],
    ['city', ''],
    ['province', ''],
    ['zipcode', ''],
    ['formattedLocationInfo', ''],
    ['locationName', ''],
  ]

  static observedAttributes = ['title', 'on-change', 'representation-data']

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'representation-data' && oldValue !== newValue) {
      this.getRepresentationData()
    }
  }

  getRepresentationData() {
    const language = Locale.language
    const data = JSON.parse(decodeURIComponent(this.representationData)) as any

    this.dateTimeData = data.dateTime || ''

    // location if user enters a new location manually
    this.place = data?.place || ''
    this.address = data?.address || ''
    this.city = data?.city || ''
    this.province = data?.province || ''
    this.zipcode = data?.zipcode || ''

    // location if user selects a physical location from the list
    this.placeAddress = data.placeData?.address || ''
    this.placeCity = data.placeData?.city || ''
    this.placeRegion = data.placeData?.region || ''
    this.placePostalCode = data.placeData?.postalCode || ''

    // location if user selects a virtual location from the list
    this.virtualPlaceAddress = data.virtualPlaceData?.address || ''
    this.virtualPlaceCity = data.virtualPlaceData?.city || ''
    this.virtualPlaceRegion = data.virtualPlaceData?.region || ''
    this.virtualPlacePostalCode = data.virtualPlaceData?.postalCode || ''

    const type = data.schema || data.table
    this.type = Locale.getKey(
      type === 'eventOffers'
        ? 'lvc-representation-block.offerTypeLabel'
        : 'lvc-representation-block.exhibitionTypeLabel'
    )
    this.eventName = data.name?.[language] || data[language === 'fr' ? 'nameFr' : 'nameEn']

    const getPriceTypeForExistingEvent = (): string => {
      if (data.isAvailableForFree) return 'free'
      if (data.minimumPrice) return 'minimum-price'
      if (data.price) return 'fixed-price'
      return 'none'
    }

    this.priceData = {
      price: data.minimumPrice || data.price,
      priceType: this.isExisting ? getPriceTypeForExistingEvent() : data.priceType || 'free',
    }
    this.startDate = data.startDate || ''
    this.endDate = data.endDate || ''
    this.startTime = data.startTime || ''

    // convert start date to more readable format
    const startDateValue = this.isExisting ? parseInt(this.startDate) * 1000 : this.startDate
    const startTimeValue =
      typeof this.startTime === 'string' ? this.startTime : this.startTime?.[Locale.language]
    const timeZone = this.isExisting ? 'EST' : 'UTC'

    this.startDateFormatted = this.getFormattedDateAndTime(startDateValue, startTimeValue, timeZone)

    if (this.isExisting) {
      this.formattedLocationInfo = data.location[Locale.language]
    } else {
      this.getFormattedLocationData()
    }
    this.rerender()
  }

  getFormattedDateAndTime(
    startDate: string | number,
    startTime: string,
    timeZone: 'UTC' | 'EST' = 'UTC'
  ): string {
    let dateInMilliseconds: number

    // Handle string date format (ISO date string)
    if (typeof startDate === 'string') {
      const parsedDate = new Date(startDate)
      if (isNaN(parsedDate.getTime())) {
        throw new Error(`Invalid date string format: ${startDate}`)
      }
      dateInMilliseconds = parsedDate.getTime()
    }
    // Handle numeric timestamps (seconds or milliseconds)
    else if (typeof startDate === 'number') {
      // Convert from seconds to milliseconds if needed
      // More reliable than string length check: timestamps before 1973 have <10 digits
      dateInMilliseconds = startDate < 10000000000 ? startDate * 1000 : startDate
    }
    // Handle invalid input type
    else {
      throw new Error(`Unsupported date format: ${typeof startDate}`)
    }

    try {
      const formattedDate = new Date(dateInMilliseconds).toLocaleDateString(Locale.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: timeZone,
      })

      return `${formattedDate}, ${startTime}`
    } catch (error) {
      throw new Error(`Failed to format date`)
    }
  }

  getFormattedLocationData() {
    // here we decide which location data to use based on the user input
    if (this.place) {
      this.locationName = this.place
      this.formattedLocationInfo = `${this.address}, ${this.city}, ${this.province}, ${this.zipcode}`
    } else if (this.placeAddress) {
      this.locationName = this.placeAddress
      this.formattedLocationInfo = `${this.placeCity}, ${this.placeRegion}, ${this.placePostalCode}`
    } else if (this.virtualPlaceAddress) {
      this.locationName = this.virtualPlaceAddress
      this.formattedLocationInfo = `${this.virtualPlaceCity}, ${this.virtualPlaceRegion}, ${this.virtualPlacePostalCode}`
    }
  }

  async onBlur(e: Event): Promise<void> {
    ConsoleLog(true, e.target)
    this.open = false
    this.rerender()
  }

  onClickModify(): void {
    let flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)

    if (!flowCore) {
      return
    }
    flowCore.getData().control_currentRepresentationStep = this.representationIndex
    flowCore.goTo('addRepresentation')
  }

  renderPriceTag() {
    const getPriceText = () => {
      if (this.priceData.priceType === 'free') return Locale.getKey('lvc-representation-block.free')
      if (this.priceData.priceType === 'minimum-price') {
        return `${Locale.getKey('lvc-representation-block.startingFrom')} ${this.priceData.price}$`
      }
      return `${this.priceData.price}$`
    }
    return this.priceData.priceType === 'none' || this.hidePrice
      ? ''
      : `
      <lvc-box class="priceContainer" mt="8px">
        <lvc-text font-size="12px" font-weight="700" line-height="150%" class="priceText"> 
          ${getPriceText()}
        </lvc-text>
      </lvc-box>
    `
  }

  html = () => {
    return `
   <div>
    <lvc-box display="flex" flexDirection="column" rowGap="16px" border="1px solid var(--nuit-30, rgba(30, 0, 125, 0.30))" borderRadius="8px" alignItems="flex-start" mt="${this.compact ? '0' : '16px'}" mb="${this.compact ? '0' : '16px'}" borderRadius="8px" justifyContent="center" background="#FFF">
      <lvc-box class="content" padding="0 16px" mt="16px" mb="16px" alignItems="flex-start" width="100%">
        <lvc-box class="dateContainer" alignItems="flex-start">
          <lvc-text font-preset="smallExtraBold" text-transform="uppercase" lineHeight="120%"> 
            ${this.startDateFormatted}
          </lvc-text>
        </lvc-box>

        <lvc-box class="locationContainer" mt="8px">
          <lvc-text font-size="12px" font-weight="500" line-height="150%"> 
            ${this.locationName}
          </lvc-text>
          <lvc-text font-size="12px" font-weight="500" line-height="150%"> 
            ${this.formattedLocationInfo}
          </lvc-text>
        </lvc-box>
        ${this.renderPriceTag()}
        ${
          this.showType
            ? `
          <lvc-box display="flex" justifyContent="flex-end">
            <lvc-text font-preset="small" font-size="12px">
              ${this.type} ${this.eventName ? `- ${this.eventName}` : ''}
            </lvc-text>
          </lvc-box>
        `
            : ''
        }
      </lvc-box>
    </lvc-box>

   </div>
   ${
     !this.isExisting
       ? `
    <div class="modify-container">
      <button class="modify" name="modify">${Locale.getKey('lvc-representation-block.modify')}</button>
    </div>
   `
       : ''
   }
    `
  }

  css = () => {
    return `
      :host {

      }

      .modify-container{
        display: flex;
        justify-content: flex-end;
      }


      #buttonAddImage{
        display: flex;
        padding: 13px 16px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex: 1 0 0;

      }

      .modify {
        color: #1E007D;
        font-weight: ${Font.weight.medium};
        font-size: 12px;
        line-height: 14px;
        text-decoration: underline;
        cursor: pointer;
        border: none;
        background: none;
        padding: 0;
        margin: 0;
      }

      .priceText{
        background: #BFF0FF;
        border-radius: 4px;
        padding: 5px 8px;
        width: fit-content;
      }

      


    `
  }

  eventListeners: EventListeners = [
    [this, 'blur', this.onBlur.bind(this)],
    ['[name="modify"]', 'click', this.onClickModify.bind(this)],
  ]
}
